/**
 * How the Create Invoice form derives the billing user's name (OR-1555).
 *
 * Some broker rows carry only a display `name` ("Adam Fallon") with
 * `first_name`/`last_name` null. Ascend rejects an Account User or Account
 * contact whose first OR last name is blank, and the backend fills a blank
 * one from `broker.name` with the split below (PRs #405/#406). The form
 * applies the same split up front so the user sees the name that will be
 * sent and can correct it, instead of retyping something the system knows.
 */

import { type PersonName, splitBrokerName } from "../../../utils/person-name";

export type BillingUserName = PersonName;

/** The split lives in `utils/person-name` so the add-broker modal stores
 *  names with the same rule this form uses to display them (OR-1600). */
export { splitBrokerName };

function nonBlank(value: string | null | undefined): string | null {
    const trimmed = (value ?? "").trim();
    return trimmed === "" ? null : trimmed;
}

/**
 * Name to seed the editable inputs with when a broker is selected.
 *
 * Priority per field: a name the user already confirmed (a restored draft),
 * then the broker's own first/last name, then the split of `broker.name`.
 * The fallback is applied field by field, so a broker with a first name but
 * no last name only gets the last name derived.
 *
 * A confirmed value wins even when blank: the user cleared it on purpose
 * and saved, so the form must not quietly refill it with a name they
 * removed. Validation asks for it again instead.
 */
export function billingUserNameFor(
    broker: { name?: string | null; first_name?: string | null; last_name?: string | null } | null | undefined,
    confirmed?: BillingUserName | null,
): BillingUserName {
    const derived = splitBrokerName(broker?.name);
    return {
        first: confirmed ? confirmed.first.trim() : (nonBlank(broker?.first_name) ?? derived.first),
        last: confirmed ? confirmed.last.trim() : (nonBlank(broker?.last_name) ?? derived.last),
    };
}
